import { toNestErrors } from "@hookform/resolvers";
import { deepSanitizeWithExclusions } from "@congrs/utils";
import { appendErrors } from "react-hook-form";
import ajv from "./ajvInstance.js";

const FORMAT_MESSAGES = {
  email: "Invalid email address",
  date: "Invalid date",
  "iso-date-time": "Invalid date/time",
};

const formatErrorMessage = (error) => {
  const { keyword, params, data } = error;

  switch (keyword) {
    case "errorMessage":
      return error.message;
    case "required":
    case "i18nRequired":
      return "This field is required";
    case "minLength":
      return params.limit === 1
        ? "This field is required"
        : `Must be at least ${params.limit} characters`;
    case "maxLength":
      return `Must be no more than ${params.limit} characters`;
    case "minimum":
      return `Must be at least ${params.limit}`;
    case "maximum":
      return `Must be no more than ${params.limit}`;
    case "format":
      return FORMAT_MESSAGES[params.format] || "Invalid format";
    case "pattern":
      return "Invalid format";
    case "type":
      return data === null || data === undefined || data === ""
        ? "This field is required"
        : "Invalid value";
    case "enum":
      return "Must be one of the allowed values";
    case "const":
      return "Value does not match";
    default:
      return error.message;
  }
};

const parseErrorSchema = (ajvErrors, validateAllFieldCriteria, data) => {
  ajvErrors.forEach((error) => {
    if (error.keyword === "required") {
      error.instancePath += "/" + error.params.missingProperty;
    }
    // For i18nRequired errors, set the path to the first language code field
    if (error.keyword === "i18nRequired") {
      // Get the i18n object from the data using the error path
      const pathParts = error.instancePath.substring(1).split("/");
      let i18nData = data;
      for (const part of pathParts) {
        i18nData = i18nData?.[part];
      }
      // Use the first language key if available, otherwise default to 'en'
      const firstLangKey =
        i18nData && typeof i18nData === "object"
          ? Object.keys(i18nData)[0] || "en"
          : "en";
      error.instancePath += "/" + firstLangKey;
    }
  });

  return ajvErrors.reduce((previous, error) => {
    const path = error.instancePath.substring(1).replace(/\//g, ".");

    if (!previous[path]) {
      previous[path] = {
        message: formatErrorMessage(error),
        type: error.keyword,
      };
    }

    if (validateAllFieldCriteria) {
      const types = previous[path].types;
      const messages = types && types[error.keyword];

      previous[path] = appendErrors(
        path,
        validateAllFieldCriteria,
        previous,
        error.keyword,
        messages
          ? [].concat(messages, formatErrorMessage(error) || "")
          : formatErrorMessage(error)
      );
    }

    return previous;
  }, {});
};

const resolver = (schema) => {
  const validate = ajv.compile(schema);

  return (data, _, options) => {
    const cleaned = deepSanitizeWithExclusions(data);
    const valid = validate(cleaned);

    const result = valid
      ? { values: cleaned, errors: {} }
      : {
          values: {},
          errors: toNestErrors(
            parseErrorSchema(
              validate.errors,
              !options.shouldUseNativeValidation &&
                options.criteriaMode === "all",
              cleaned
            ),
            options
          ),
        };

    return result;
  };
};

export default resolver;
